import {inject, Injectable} from '@angular/core';
import {Observable, throwError, TimeoutError} from 'rxjs';
import {catchError, map, timeout as rxTimeout} from 'rxjs/operators';
import {BaseHttpService} from '@shared/infrastructure/services/http/base-http.service';
import {environment} from '@env/environment';
import {PaymentPollingStatus} from '@features/payment/domain/model/payment.model';
import {PaymentPollingTimeoutError, PaymentStatusNetworkError} from '@features/payment/domain/model/payment.errors';
import {NetworkConnectionService} from '@core/services/network-connection/network-connection.service';

interface StatusResponse {
  status: PaymentPollingStatus;
}

interface PhysicalSecretResponse {
  secret: string;
}

@Injectable({providedIn: 'root'})
export class BatchPaymentsGateway extends BaseHttpService {
  private readonly network = inject(NetworkConnectionService);
  protected readonly baseUrl = environment.apiURL;
  private readonly HTTP_REQUEST_TIMEOUT = 5000;

  getStatus(batchId: string): Observable<PaymentPollingStatus> {
    if (!this.network.isOnline) {
      return throwError(() => new PaymentStatusNetworkError());
    }

    return this.get<StatusResponse>(`/api/v2/batch-payments/${batchId}/status`).pipe(
      rxTimeout(this.HTTP_REQUEST_TIMEOUT),
      map(response => response.status),
      catchError(error => this.handleError(error))
    );
  }

  getPhysicalPaymentSecret(batchId: string): Observable<string> {
    return this.get<PhysicalSecretResponse>(`/api/v2/batch-payments/${batchId}/physical-payment-secret`).pipe(
      map(response => response.secret)
    );
  }

  private handleError(error: unknown): Observable<never> {
    return error instanceof TimeoutError
      ? throwError(() => new PaymentPollingTimeoutError())
      : throwError(() => new PaymentStatusNetworkError());
  }
}
